import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface CarImageUploadDialogData {
  currentFileName?: string | null;
  modelId: string;
}

export interface CarImageUploadDialogResult {
  success: boolean;
  file?: File;
  fileName?: string;
  deleted?: boolean;
}

@Component({
  selector: 'app-car-image-upload-dialog',
  templateUrl: './car-image-upload-dialog.component.html',
  styleUrls: ['./car-image-upload-dialog.component.scss'],
})
export class CarImageUploadDialogComponent {
  uploadedFile: File | null = null;
  uploadedFileName: string | null = null;
  isDragOver = false;
  hadExistingImage = false;

  constructor(
    private dialogRef: MatDialogRef<CarImageUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CarImageUploadDialogData,
    private snackBar: MatSnackBar,
  ) {
    // Initialize with existing image if present
    if (data.currentFileName) {
      this.uploadedFileName = data.currentFileName;
      this.hadExistingImage = true;
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  handleFile(file: File): void {
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];

    if (!validTypes.includes(file.type)) {
      this.snackBar.open(
        'Érvénytelen fájlformátum. Kérjük, PNG vagy JPG/JPEG formátumú képet töltsön fel.',
        'Bezárás',
        { duration: 5000 }
      );
      return;
    }

    this.uploadedFile = file;
    this.uploadedFileName = file.name;
  }

  openFileInput(): void {
    const fileInput = document.getElementById('car-image-file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  get hasChanges(): boolean {
    // Has changes if: new file selected OR existing image was cleared
    return !!this.uploadedFile || (this.hadExistingImage && !this.uploadedFileName);
  }

  onCancel(): void {
    this.dialogRef.close({ success: false });
  }

  onSave(): void {
    if (!this.hasChanges) {
      return;
    }

    // Case 1: New file selected
    if (this.uploadedFile) {
      this.dialogRef.close({
        success: true,
        file: this.uploadedFile,
        fileName: this.uploadedFileName || this.uploadedFile.name,
      } as CarImageUploadDialogResult);
      return;
    }

    // Case 2: Existing image was cleared (deleted)
    if (this.hadExistingImage && !this.uploadedFileName) {
      this.dialogRef.close({
        success: true,
        deleted: true,
      } as CarImageUploadDialogResult);
      return;
    }
  }

  clearSelectedFile(): void {
    // Soft delete within dialog - just clear the selection
    this.uploadedFile = null;
    this.uploadedFileName = null;
  }
}
